'use client';

import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Ruler, ShieldCheck, Sparkles } from 'lucide-react';
import { PublicLayout } from '@/components/Layout/PublicLayout';
import { Loader } from '@/components/ui/loader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useMerchCheckoutMutation,
  usePublicMerchProductDetailQuery,
} from '@/hooks/api/useMerch';
import { CheckoutModal } from '../components/CheckoutModal';
import { MerchStoreTheme } from '../components/MerchStoreTheme';
import { ProductImageGallery } from '../components/ProductImageGallery';
import { SizeChartModal } from '../components/SizeChartModal';
import { SizeSelector } from '../components/SizeSelector';
import type { PublicMerchProductDetail } from '../interfaces/merch';
import { MERCH_CATEGORIES } from '../interfaces/merch';
import { toast } from 'sonner';

interface MerchProductDetailPageProps {
  slug: string;
  productId: string;
  initialData?: PublicMerchProductDetail;
}

function formatPrice(value: number) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
}

function getCategoryLabel(category?: string) {
  return MERCH_CATEGORIES.find((c) => c.value === category)?.label ?? category;
}

export function MerchProductDetailPage({ slug, productId, initialData }: MerchProductDetailPageProps) {
  const { data, isLoading } = usePublicMerchProductDetailQuery(slug, productId, initialData);
  const checkoutMutation = useMerchCheckoutMutation(slug);
  const [selectedSize, setSelectedSize] = useState<string | undefined>();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [sizeChartOpen, setSizeChartOpen] = useState(false);

  const detail = data ?? initialData;
  const organization = detail?.organization;
  const product = detail?.product;

  const variants = product?.variants ?? [];
  const hasVariants = variants.length > 0;

  const selectedVariant = useMemo(
    () => variants.find((v) => v.size === selectedSize),
    [variants, selectedSize],
  );

  const maxStock = hasVariants
    ? (selectedVariant?.stock_quantity ?? 0)
    : (product?.stock_quantity ?? 0);

  const outOfStock = maxStock <= 0;

  const handleCheckout = async (payload: {
    buyer_email: string;
    buyer_name?: string;
    quantity: number;
    selected_size?: string;
  }) => {
    if (!product) return;

    try {
      const response = await checkoutMutation.mutateAsync({
        product_id: product.id,
        ...payload,
      });
      window.location.href = response.init_point || response.sandbox_init_point || '';
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Error al procesar la compra';
      toast.error(message);
    }
  };

  const handleBuyClick = () => {
    if (hasVariants && !selectedSize) {
      toast.error('Selecciona una talla para continuar');
      return;
    }
    setCheckoutOpen(true);
  };

  if (isLoading && !detail) {
    return (
      <PublicLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader text="Cargando producto..." />
        </div>
      </PublicLayout>
    );
  }

  if (!organization || !product) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-7xl px-4 py-24 text-center">
          <h1 className="text-2xl font-semibold">Producto no encontrado</h1>
          <p className="mt-2 text-muted-foreground">
            Este producto no existe o ya no está disponible.
          </p>
          <Button className="mt-6" asChild>
            <Link to={`/merch/${slug}`}>Volver a la tienda</Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <MerchStoreTheme organization={organization}>
        <div className="relative overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-80"
            style={{
              background: `
                radial-gradient(ellipse 70% 50% at 0% 0%, color-mix(in srgb, var(--merch-accent) 18%, transparent), transparent 55%),
                radial-gradient(ellipse 50% 40% at 100% 20%, color-mix(in srgb, var(--merch-accent-secondary) 12%, transparent), transparent 50%)
              `,
            }}
          />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <Link
              to={`/merch/${slug}`}
              className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a {organization.name}
            </Link>

            <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
              <div className="lg:sticky lg:top-8 lg:self-start">
                <ProductImageGallery
                  images={product.images ?? []}
                  productName={product.name}
                  fallbackUrl={product.image_url}
                />
              </div>

              <div className="space-y-8">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {product.details?.category && (
                      <Badge variant="outline" className="uppercase tracking-wider">
                        {getCategoryLabel(product.details.category)}
                      </Badge>
                    )}
                    {product.details?.is_featured && (
                      <Badge className="gap-1 bg-[var(--merch-accent)] text-white border-0">
                        <Sparkles className="h-3 w-3" />
                        Destacado
                      </Badge>
                    )}
                    {outOfStock && <Badge variant="destructive">Agotado</Badge>}
                  </div>

                  <h1 className="font-[family-name:var(--font-merch-display)] text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl">
                    {product.name}
                  </h1>

                  <p className="text-3xl font-bold text-[var(--merch-accent)]">
                    {formatPrice(Number(product.price))}
                  </p>

                  {product.description && (
                    <p className="text-base leading-relaxed text-muted-foreground">
                      {product.description}
                    </p>
                  )}
                </div>

                {hasVariants && (
                  <SizeSelector
                    variants={variants}
                    selectedSize={selectedSize}
                    onSelect={setSelectedSize}
                  />
                )}

                <div className="flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="min-w-[12rem] bg-[var(--merch-accent)] text-white hover:bg-[color-mix(in_srgb,var(--merch-accent)_85%,black)]"
                    disabled={outOfStock}
                    onClick={handleBuyClick}
                  >
                    {outOfStock ? 'Sin stock' : 'Comprar ahora'}
                  </Button>

                  <Button
                    type="button"
                    size="lg"
                    variant="outline"
                    className="gap-2"
                    onClick={() => setSizeChartOpen(true)}
                  >
                    <Ruler className="h-4 w-4" />
                    Guía de tallas
                  </Button>
                </div>

                {!outOfStock && (
                  <p className="text-sm text-muted-foreground">
                    {hasVariants && selectedSize
                      ? `${maxStock} unidades disponibles en talla ${selectedSize}`
                      : `${maxStock} unidades en stock`}
                  </p>
                )}

                <div className="space-y-4 rounded-2xl border border-white/10 bg-card/30 p-6">
                  {product.details?.material && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                        Material
                      </p>
                      <p className="mt-1 text-sm">{product.details.material}</p>
                    </div>
                  )}

                  {product.details?.care_instructions && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                        Cuidado
                      </p>
                      <p className="mt-1 text-sm whitespace-pre-line">
                        {product.details.care_instructions}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ShieldCheck className="h-4 w-4 text-[var(--merch-accent)]" />
                    Pago seguro con Mercado Pago · Envío coordinado por la organización
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <CheckoutModal
          product={product}
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          onConfirm={handleCheckout}
          isLoading={checkoutMutation.isPending}
          selectedSize={selectedSize}
          onSizeChange={setSelectedSize}
        />

        <SizeChartModal
          open={sizeChartOpen}
          onClose={() => setSizeChartOpen(false)}
          sizeChart={product.details?.size_chart}
        />
      </MerchStoreTheme>
    </PublicLayout>
  );
}
